import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import React, { useContext, useMemo, useState } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { DealerSwitcherContext } from './DealerSwitcherContext'

const HEADING = 'text-lg font-semibold leading-7 tracking-[-0.5px] text-white'

// Stands in for "no dealer" in the list, so All dealers is a row like any other.
const ALL_DEALERS = { id: null, label: 'All dealers' }

/**
 * The page heading, which is also the dealer switcher: it reads as the dealer's name and
 * opens a searchable dealer list on click. This is how the old dealer header worked
 * before WARP-920 moved the switcher up into the nav bar, plus a search box, since a WPC
 * executive can reach several hundred dealers.
 *
 * `label` is what shows when no dealer is picked (the Ready Queue's "All dealers").
 * With nothing else to pick, it is a plain heading.
 */
const DealerSwitcher = ({ dealer, label }) => {
  const { canShowAll, dealerOptions, marksRoIntake, onSwitch } = useContext(DealerSwitcherContext)
  const [query, setQuery] = useState('')
  const name = dealer?.name || label

  const choices = useMemo(
    () => (canShowAll && dealer ? [ALL_DEALERS, ...dealerOptions] : dealerOptions),
    [canShowAll, dealer, dealerOptions],
  )
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return choices
    return choices.filter((option) => option.label.toLowerCase().includes(needle))
  }, [choices, query])

  const current = choices.find((option) => option.id === (dealer?.id ?? null)) || null
  const canSwitch =
    Boolean(onSwitch) && choices.some((option) => option.id !== (dealer?.id ?? null))

  if (!canSwitch) return <h1 className={HEADING}>{name}</h1>

  // The button sits inside the heading, not the other way round: a heading is not
  // allowed inside a button, and this way the page keeps its h1.
  return (
    <h1 className={HEADING}>
      <Popover className="relative">
        {({ close }) => (
          <>
            <PopoverButton className="group flex items-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60">
              <span className="decoration-white/40 underline-offset-4 group-hover:underline">
                {name}
              </span>
              <ChevronDownIcon
                aria-hidden="true"
                className="h-5 w-5 text-white/60 transition-colors group-hover:text-white"
              />
              <span className="sr-only">, switch dealer</span>
            </PopoverButton>

            <PopoverPanel
              anchor="bottom start"
              className="z-40 mt-2 w-[400px] max-w-[calc(100vw-2rem)] rounded-lg bg-white text-base font-normal tracking-normal shadow-lg ring-1 ring-black/5 focus:outline-none"
            >
              <Combobox
                by="id"
                onChange={(option) => {
                  if (!option) return
                  setQuery('')
                  close()
                  if (option.id !== (dealer?.id ?? null)) onSwitch(option.id)
                }}
                value={current}
              >
                <div className="border-charcoal-100 flex items-center gap-2 border-b px-3">
                  <MagnifyingGlassIcon aria-hidden="true" className="text-charcoal-400 h-4 w-4" />
                  <ComboboxInput
                    aria-label="Search dealers"
                    autoComplete="off"
                    autoFocus
                    className="text-charcoal-900 placeholder:text-charcoal-400 w-full border-0 bg-transparent py-2.5 text-sm focus:outline-none focus:ring-0"
                    displayValue={() => query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search dealers"
                  />
                </div>

                <ComboboxOptions className="max-h-72 overflow-auto py-1" static>
                  {filtered.map((option) => (
                    <ComboboxOption
                      className={mergeClassNames(
                        'text-charcoal-900 flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm data-[focus]:bg-blue-50',
                        option.id === null && 'border-charcoal-100 border-b font-semibold',
                      )}
                      key={option.id ?? 'all'}
                      value={option}
                    >
                      {({ selected }) => (
                        <>
                          <span className="truncate">{option.label}</span>
                          {marksRoIntake && option.isRoIntakeEnabled && (
                            <span
                              className="ml-auto shrink-0 rounded bg-blue-50 px-1.5 py-px text-[10px] font-semibold text-blue-800"
                              title="This dealer logs ROs through RO Intake, so it has a dashboard and a Work Queue."
                            >
                              RO Intake
                            </span>
                          )}
                          {selected && (
                            <CheckIcon
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0 text-blue-800"
                            />
                          )}
                        </>
                      )}
                    </ComboboxOption>
                  ))}

                  {filtered.length === 0 && (
                    <p className="text-charcoal-400 p-3 text-sm">No dealers match.</p>
                  )}
                </ComboboxOptions>
              </Combobox>
            </PopoverPanel>
          </>
        )}
      </Popover>
    </h1>
  )
}

DealerSwitcher.propTypes = {
  dealer: PropTypes.object,
  label: PropTypes.string,
}

DealerSwitcher.defaultProps = {
  dealer: null,
  label: '',
}

export default DealerSwitcher
